// src/services/auth/authService.js
import easyCheckApi, { easyCheckRequest } from '../api/config';
import { handleApiError } from '../../utils/errorHandler';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';

export const easyCheckAuthService = {
  login: async (credentials) => {
    // Include device fingerprint so backend can check trusted devices
    const deviceFingerprint = getDeviceFingerprint();
    const result = await easyCheckRequest(() =>
      easyCheckApi.post('/login', { ...credentials, deviceFingerprint })
    );

    if (result.success) {
      // Check if OTP is required
      const user = result.data.user || result.data;
      if (user.requiresOtp) {
        // Don't store token — OTP verification needed
        return { success: true, requiresOtp: true, data: result.data };
      }

      // Handle different possible response structures
      let token;

      if (result.data.token) {
        // Token at root level
        token = result.data.token;
      } else if (user.token) {
        // Token nested under user
        token = user.token;
      } else {
        console.error('No token found in login response:', result.data);
        return { success: false, message: 'No authentication token received' };
      }

      // Store token and user data
      localStorage.setItem('easyCheckToken', token);
      localStorage.setItem('easyCheckUser', JSON.stringify(user));

      console.log('🔐 Token stored successfully:', { token: token.substring(0, 20) + '...', user });
    }

    return result;
  },

  verifyOtp: async ({ email, password, otp, trustDevice }) => {
    const deviceFingerprint = getDeviceFingerprint();
    const result = await easyCheckRequest(() =>
      easyCheckApi.post('/VerifyOtp', {
        email,
        password,
        otp,
        deviceFingerprint,
        trustDevice
      })
    );

    if (result.success) {
      const user = result.data.user || result.data;
      const token = user.token || result.data.token;

      if (token) {
        localStorage.setItem('easyCheckToken', token);
        localStorage.setItem('easyCheckUser', JSON.stringify(user));
      }
    }

    return result;
  },

  resendOtp: async (email) => {
    return easyCheckRequest(() =>
      easyCheckApi.post('/ResendOtp', { email })
    );
  },

  logout: async () => {
    const result = await easyCheckRequest(() => 
      easyCheckApi.post('/logout')
    );
    
    // Clear all EasyCheck stored data
    localStorage.removeItem('easyCheckToken');
    localStorage.removeItem('easyCheckUser');
    localStorage.removeItem('easyCheckLoginTime');
    localStorage.removeItem('easyCheckRedirectUrl');
    
    return result;
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('easyCheckRefreshToken');
    return easyCheckRequest(() => 
      easyCheckApi.post('/refresh', { refreshToken })
    );
  },

  validateSession: async () => {
    return easyCheckRequest(() => 
      easyCheckApi.get('/validate')
    );
  },

  // Updated signup method to match UserSignup API spec
  signup: async (userData) => {
    // Transform data to match API specification
    const signupData = {
      username: userData.username, // email
      password: userData.password,
      firstName: userData.firstName,
      lastName: userData.lastName,
      companyName: userData.companyName,
      phoneNumber: userData.phoneNumber || undefined // optional field
    };

    return easyCheckRequest(() => 
      easyCheckApi.post('/usersignup', signupData)
    );
  },

  // Email verification methods
  verifyEmail: async (username, verificationToken) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/usersignup/verifyemail', {
        username,
        verificationToken
      })
    );
  },

  resendVerificationEmail: async (username) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/usersignup/resendemail', {
        username
      })
    );
  },

  changePassword: async (passwordData) => {
    return easyCheckRequest(() => 
      easyCheckApi.put('/usersignup/changepassword', passwordData)
    );
  },

  // Password reset methods
  forgotPassword: async (username) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/usersignup/forgotpassword', { username })
    );
  },

  resetPassword: async (resetData) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/usersignup/resetpassword', resetData)
    );
  },

  // Company management methods (require authentication)
  addUserToCompany: async (userCompanyData) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/usersignup/addtocompany', userCompanyData)
    );
  },

  getCompanies: async (pageNumber = 1, pageSize = 10) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/usersignup/companies?pageNumber=${pageNumber}&pageSize=${pageSize}`)
    );
  }
}; 